import React, { useEffect, useState } from 'react';
import { Clock, X as XIcon, RefreshCw } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import brokerService from '../../services/brokerService';
import type { BrokerAccount } from '../../types';
import {
  formatCheckStart,
  bucketCheckStartUpdates,
  CHECK_START_RANGE_LABEL,
} from '../../utils/checkStart';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  accounts: BrokerAccount[];
  /** Called after every account is saved successfully. */
  onSaved: () => void;
}

/**
 * Self-service "Checks start time" modal — the tenant-agnostic counterpart to
 * the full EditBrokerAccountForm (white-label users don't see the Brokers page).
 * Sets the per-account time from which the per-minute checks begin. One
 * "HH:MM" input per account, bounded to [09:16, 12:00]; unset = 09:16.
 */
const SetCheckStartDialog: React.FC<Props> = ({ isOpen, onClose, accounts, onSaved }) => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset to each account's effective start whenever the dialog opens.
  useEffect(() => {
    if (!isOpen) return;
    const next: Record<string, string> = {};
    for (const a of accounts) {
      next[a.client_id] = formatCheckStart(a.check_start_hour, a.check_start_minute);
    }
    setValues(next);
    setError(null);
  }, [isOpen, accounts]);

  const handleChange = (clientId: string, raw: string) => {
    setValues((v) => ({ ...v, [clientId]: raw }));
  };

  const handleSave = async () => {
    setError(null);
    // Bucket inputs: empty/unchanged → skip; malformed/out-of-range → block;
    // a new valid time → queue. An empty field never blocks other accounts.
    const { updates, invalid } = bucketCheckStartUpdates(accounts, values);

    if (invalid) {
      setError(`Start time must be between ${CHECK_START_RANGE_LABEL}.`);
      return;
    }
    if (updates.length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await Promise.all(
        updates.map((u) =>
          brokerService.updateBrokerAccount(u.clientId, {
            check_start_hour: u.hour,
            check_start_minute: u.minute,
          }),
        ),
      );
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to save start time. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="md" ariaLabel="Set checks start time">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              Checks start time
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            The time from which this account's per-minute checks begin — both
            management (stop-loss, target, trailing, MTM, order sync) and the
            monitored entries (Wait &amp; Trade, ORB, re-entry). Before it, those
            entries don't fire and existing positions — including carried-over
            ones — aren't managed. Scheduled entries/exits are unaffected. Range{' '}
            {CHECK_START_RANGE_LABEL}; default 09:16.
          </p>

          <div className="space-y-3">
            {accounts.map((a) => (
              <div key={a.client_id} className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                    {a.broker_name}
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-mono">{a.client_id}</span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Current: {formatCheckStart(a.check_start_hour, a.check_start_minute)}
                  </div>
                </div>
                <input
                  type="time"
                  min={formatCheckStart(9, 16)}
                  max={formatCheckStart(12, 0)}
                  step={60}
                  value={values[a.client_id] ?? ''}
                  onChange={(e) => handleChange(a.client_id, e.target.value)}
                  disabled={saving}
                  aria-label={`Checks start time for ${a.broker_name} ${a.client_id}`}
                  className="w-36 px-3 py-2 text-sm rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-60 tabular-nums"
                />
              </div>
            ))}
          </div>

          {error && (
            <div className="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={saving}
            leftIcon={saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : undefined}
          >
            {saving ? 'Saving…' : 'Save start time'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SetCheckStartDialog;
